import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Brain, X, CheckCircle, Warning, ArrowsClockwise, XCircle } from "@phosphor-icons/react";

const REC_LABELS = {
    keep: { label: "継続使用 (Keep)", cls: "ok", Icon: CheckCircle },
    monitor: { label: "経過観察 (Monitor)", cls: "info", Icon: Warning },
    replace_soon: { label: "近日中の買替推奨 (Replace Soon)", cls: "warn", Icon: ArrowsClockwise },
    replace_now: { label: "即時買替推奨 (Replace Now)", cls: "ng", Icon: XCircle },
};

const RISK_LABELS = {
    low: "低", medium: "中", high: "高",
};

export default function ReplacementReportDialog({ vehicleId, open, onClose }) {
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState(null);

    const generate = async () => {
        setLoading(true);
        setData(null);
        try {
            const { data } = await api.get(`/ai/replacement/${vehicleId}`);
            setData(data);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "AI分析に失敗しました");
        } finally {
            setLoading(false);
        }
    };

    if (!open) return null;
    const rec = data?.ai?.recommendation && REC_LABELS[data.ai.recommendation];

    return (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="replace-dialog">
            <div className="prc-card p-6 max-w-2xl w-full max-h-[90vh] overflow-auto">
                <div className="flex justify-between items-start">
                    <div>
                        <p className="prc-label-overline mb-1">AI 買替判断レポート</p>
                        <h2 className="text-xl font-bold">車両ライフサイクル分析</h2>
                        <p className="text-xs text-[var(--prc-muted)] mt-1">維持費・年式・点検NG履歴をもとに AI が客観的判断を出力します。</p>
                    </div>
                    <button onClick={onClose} className="text-[var(--prc-muted)] hover:text-[var(--prc-ink)]" data-testid="replace-close-btn"><X size={24} /></button>
                </div>

                {!data && !loading && (
                    <div className="mt-6">
                        <button onClick={generate} className="prc-btn-primary w-full inline-flex items-center justify-center gap-2" data-testid="replace-generate-btn">
                            <Brain size={24} /> AI分析を実行
                        </button>
                        <p className="mt-3 text-xs text-[var(--prc-muted)]">分析には数秒かかります。Emergent LLM (GPT-4o mini) を使用します。</p>
                    </div>
                )}

                {loading && (
                    <div className="mt-8 text-center" data-testid="replace-loading">
                        <Brain size={24} className="mx-auto animate-pulse text-[var(--prc-accent)]" />
                        <p className="mt-3 text-sm">AI が分析中…</p>
                    </div>
                )}

                {data && rec && (
                    <div className="mt-5 space-y-4" data-testid="replace-result">
                        <div className="prc-card p-4 border-2 border-[var(--prc-ink)]">
                            <div className="flex items-center gap-2">
                                <rec.Icon size={24} weight="bold" />
                                <span className={`prc-badge ${rec.cls} text-base`}>{rec.label}</span>
                                {data.ai.risk_level && <span className="prc-badge muted ml-auto">リスク: {RISK_LABELS[data.ai.risk_level] || data.ai.risk_level}</span>}
                            </div>
                            <p className="mt-3 font-semibold leading-relaxed" data-testid="replace-summary">{data.ai.summary}</p>
                        </div>

                        <div>
                            <p className="prc-label-overline mb-2">判断根拠</p>
                            <ul className="text-sm space-y-2">
                                {(data.ai.reasoning || []).map((r, i) => (
                                    <li key={i} className="flex gap-2" data-testid={`replace-reason-${i}`}>
                                        <span className="font-mono text-[var(--prc-tertiary)]">{String(i + 1).padStart(2, "0")}</span>
                                        <span className="leading-relaxed">{r}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                            <div className="prc-card p-3">
                                <p className="prc-label-overline">推定残使用月数</p>
                                <p className="prc-metric text-2xl mt-1">{data.ai.estimated_remaining_useful_life_months ?? "-"} <span className="text-xs font-sans font-normal text-[var(--prc-muted)]">ヶ月</span></p>
                            </div>
                            <div className="prc-card p-3">
                                <p className="prc-label-overline">推定年間維持費</p>
                                <p className="prc-metric text-2xl mt-1">¥{(data.ai.estimated_annual_cost_jpy || 0).toLocaleString()}</p>
                            </div>
                        </div>

                        <details className="prc-card p-3">
                            <summary className="prc-label-overline cursor-pointer">分析データ詳細</summary>
                            <dl className="text-xs mt-2 space-y-1">
                                <Row k="年式" v={`${data.stats.year || "-"} (${data.stats.age_years}年経過)`} />
                                <Row k="車検満了" v={data.stats.inspection_expiry || "-"} />
                                <Row k="累計整備費" v={`¥${data.stats.maintenance_total_cost_jpy.toLocaleString()}`} />
                                <Row k="直近12ヶ月維持費" v={`¥${data.stats.maintenance_last_12months_cost_jpy.toLocaleString()}`} />
                                <Row k="点検実施件数" v={`${data.stats.inspection_total_count} 件 (NG: ${data.stats.inspection_ng_count}, 警告: ${data.stats.inspection_warn_count})`} />
                            </dl>
                        </details>

                        <div className="flex justify-end">
                            <button onClick={onClose} className="prc-btn-primary" data-testid="replace-done-btn">閉じる</button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

function Row({ k, v }) {
    return (
        <div className="flex">
            <dt className="w-44 text-[var(--prc-muted)]">{k}</dt>
            <dd className="flex-1 font-mono">{v}</dd>
        </div>
    );
}
